import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlFilters, useUrlState } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api from '../../utils/api';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faTimes, faUsers, faChartSimple, faTriangleExclamation,
    faCircleCheck, faClock, faCircleQuestion, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import DateRangeFilter from '../../components/DateRangeFilter';
import Avatar from '../../components/Avatar';
import { useRecentSearches } from '../../components/RecentSearches';
import { orgLabel, normDept, byDepartment } from '../../utils/orgTaxonomy';
import { useOrgOptions } from '../../utils/orgOptions';
import '../../styles/App.css';
import '../../styles/tasks.css';
import '../../styles/performance.css';

/**
 * The Performance board — feature draft F6.1.
 *
 * Reads as the Task Report's workload board on purpose: same cards, same
 * department grouping, same toolbar. That board answers "what about today";
 * this one answers "how have they been doing", and the two being visually
 * continuous is what makes the second one legible at a glance.
 *
 * Everything is fetched in one request and filtered here. The roster is tens
 * of people, not thousands, and department grouping doesn't compose with
 * server paging — a "page" would slice a department in half.
 */

// Rolling last 30 days. 'Month' is the server's own name for that window
// (utils/taskDateFilter.js), not a calendar month.
const DEFAULT_PERIOD = 'Month';

const BAND_OPTIONS = [
    { value: 'All', label: 'All scores' },
    { value: 'strong', label: 'Strong — 85%+' },
    { value: 'fair', label: 'Fair — 60-84%' },
    { value: 'weak', label: 'Needs attention — under 60%' },
    { value: 'none', label: 'No score yet' }
];

const SORT_OPTIONS = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'worst', label: 'Lowest score first' },
    { value: 'best', label: 'Highest score first' },
    { value: 'busiest', label: 'Most tasks first' }
];

// The period the score covers, said in words. The number is meaningless
// without it, and "80%" alone invites the reader to assume all-time.
const PERIOD_LABEL = {
    Today: 'due today',
    Yesterday: 'due yesterday',
    Week: 'due in the last 7 days',
    Month: 'due in the last 30 days',
    All: 'all time',
    Custom: 'in the selected range'
};

const Performance = () => {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // Server-side: these change which tasks are scored, so they must go back.
    const [period, setPeriod] = useUrlState('period', DEFAULT_PERIOD);
    const [customRange, setCustomRange] = useUrlFilters({ from: '', to: '' });
    const [projectId, setProjectId] = useUrlState('project', 'All');
    const [projects, setProjects] = useState([]);

    // Client-side: these only narrow what is already here.
    // Filters on the box at once; the address catches up when typing pauses,
    // so Back from someone's page keeps the search.
    const [savedSearch, setSavedSearch] = useUrlState('q', '');
    const [searchTerm, setSearchTerm] = useState(savedSearch);
    useEffect(() => {
        const timer = setTimeout(() => setSavedSearch(searchTerm.trim()), 500);
        return () => clearTimeout(timer);
    }, [searchTerm, setSavedSearch]);
    const recentSearch = useRecentSearches('performance', { value: searchTerm, onPick: setSearchTerm });
    const [department, setDepartment] = useUrlState('dept', 'All');
    const [band, setBand] = useUrlState('band', 'All');
    const [sort, setSort] = useUrlState('sort', 'name');

    useEffect(() => {
        api.get('/tasks/assignable-projects')
            .then(res => setProjects(res.data || []))
            .catch(() => setProjects([]));
    }, []);

    // A half-finished Custom range would send an open-ended window and make the
    // board flicker between answers while the second date is being picked.
    const rangeReady = period !== 'Custom' || customRange.from || customRange.to;

    const load = useCallback(() => {
        if (!rangeReady) return;
        setLoading(true);

        api.get('/analytics/performance', {
            params: {
                filterType: period,
                fromDate: period === 'Custom' ? customRange.from : undefined,
                toDate: period === 'Custom' ? customRange.to : undefined,
                projectId: projectId !== 'All' ? projectId : undefined
            }
        })
            .then(res => {
                setRows(res.data.data || []);
                setSummary(res.data.summary || null);
            })
            .catch(err => {
                console.error('Failed to load performance board', err);
                Swal.fire('Error', 'Could not load the performance board.', 'error');
            })
            .finally(() => setLoading(false));
    }, [period, customRange.from, customRange.to, projectId, rangeReady]);

    useEffect(() => { load(); }, [load]);

    // Department order set by the admin in Admin Settings.
    const { departments: orgDepartments } = useOrgOptions();
    const deptOrder = useMemo(() => byDepartment(orgDepartments), [orgDepartments]);

    const departmentOptions = useMemo(
        () => [...new Set(rows.map(r => normDept(r.department)))].sort(deptOrder),
        [rows, deptOrder]
    );

    const filteredRows = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();

        const filtered = rows.filter(r => {
            if (department !== 'All' && normDept(r.department) !== department) return false;
            if (band !== 'All' && r.band !== band) return false;
            if (q) {
                const hay = [r.name, r.employeeId, r.jobTitle, normDept(r.department)]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });

        // Unscored people always sort last, whichever direction is chosen —
        // they are an absence of data, and floating them to the top of "lowest
        // first" would accuse them of something the data does not say.
        const byName = (a, b) => a.name.localeCompare(b.name);
        const byScore = (dir) => (a, b) => {
            if (a.score === null && b.score === null) return byName(a, b);
            if (a.score === null) return 1;
            if (b.score === null) return -1;
            return (a.score - b.score) * dir || byName(a, b);
        };

        const sorters = {
            name: byName,
            worst: byScore(1),
            best: byScore(-1),
            busiest: (a, b) => b.counts.total - a.counts.total || byName(a, b)
        };

        return [...filtered].sort(sorters[sort] || byName);
    }, [rows, searchTerm, department, band, sort]);

    const grouped = useMemo(() => {
        const map = new Map();
        filteredRows.forEach(r => {
            const d = normDept(r.department);
            if (!map.has(d)) map.set(d, []);
            map.get(d).push(r);
        });
        return [...map.entries()].sort(([a], [b]) => deptOrder(a, b));
    }, [filteredRows, deptOrder]);

    const activeFilterCount =
        (searchTerm ? 1 : 0) + (department !== 'All' ? 1 : 0) +
        (band !== 'All' ? 1 : 0) + (projectId !== 'All' ? 1 : 0);

    const clearFilters = () => {
        setSearchTerm(''); setDepartment('All'); setBand('All'); setProjectId('All');
    };

    // Changing any filter remounts the sections below, which both replays the
    // entrance animation and guarantees no card keeps a stale score on screen.
    const viewKey = `${searchTerm}|${department}|${band}|${sort}|${period}|${projectId}`;

    const periodLabel = PERIOD_LABEL[period] || 'in the selected range';

    return (
        <div className="attendance-container fade-in">
            <div className="task-page-header">
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faChartSimple} style={{ marginRight: '10px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                    Performance
                </h1>
                <div className="task-view-group">
                    <select
                        className={`task-filter-select ${projectId !== 'All' ? 'is-active' : ''}`}
                        value={projectId}
                        onChange={(e) => { setProjectId(e.target.value); recordRecentPick('project', e.target.value); }}
                    >
                        <option value="All">All Projects</option>
                        <RecentOptions kind="project" items={projects} />
                    </select>
                </div>
            </div>

            <p className="perf-lede">
                On-time completion rate for work <strong>{periodLabel}</strong>. A task counts once
                it is finished or its deadline has passed — work still in hand is left out of the
                score rather than held against anyone.
            </p>

            <DateRangeFilter
                value={period}
                onChange={setPeriod}
                custom={customRange}
                onCustomChange={setCustomRange}
            />

            {summary && (
                <div className="emp-stat-row">
                    <button
                        type="button"
                        className={`emp-stat ${band === 'All' ? 'active' : ''}`}
                        onClick={() => setBand('All')}
                    >
                        <span className="emp-stat-icon all"><FontAwesomeIcon icon={faUsers} /></span>
                        <span className="emp-stat-text">
                            <strong>{summary.avgScore === null ? '—' : `${summary.avgScore}%`}</strong>
                            <small>Company on-time rate</small>
                        </span>
                    </button>
                    <button
                        type="button"
                        className={`emp-stat ${band === 'strong' ? 'active' : ''}`}
                        onClick={() => setBand(band === 'strong' ? 'All' : 'strong')}
                    >
                        <span className="emp-stat-icon done"><FontAwesomeIcon icon={faCircleCheck} /></span>
                        <span className="emp-stat-text"><strong>{summary.strong}</strong><small>Strong — 85%+</small></span>
                    </button>
                    <button
                        type="button"
                        className={`emp-stat ${band === 'fair' ? 'active' : ''}`}
                        onClick={() => setBand(band === 'fair' ? 'All' : 'fair')}
                    >
                        <span className="emp-stat-icon today"><FontAwesomeIcon icon={faClock} /></span>
                        <span className="emp-stat-text"><strong>{summary.fair}</strong><small>Fair — 60-84%</small></span>
                    </button>
                    <button
                        type="button"
                        className={`emp-stat ${band === 'weak' ? 'active' : ''}`}
                        onClick={() => setBand(band === 'weak' ? 'All' : 'weak')}
                    >
                        <span className="emp-stat-icon late"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                        <span className="emp-stat-text"><strong>{summary.weak}</strong><small>Needs attention</small></span>
                    </button>
                    <button
                        type="button"
                        className={`emp-stat ${band === 'none' ? 'active' : ''}`}
                        onClick={() => setBand(band === 'none' ? 'All' : 'none')}
                        title="Nobody had a deadline fall inside this period — not a score of zero"
                    >
                        <span className="emp-stat-icon free"><FontAwesomeIcon icon={faCircleQuestion} /></span>
                        <span className="emp-stat-text"><strong>{summary.none}</strong><small>No score yet</small></span>
                    </button>
                </div>
            )}

            <div className="task-toolbar">
                <div className="task-toolbar-search">
                    <FontAwesomeIcon icon={faSearch} className="task-search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search employees by name, ID, designation or department..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>
                <div className="task-toolbar-filters">
                    <select
                        className={`task-filter-select ${department !== 'All' ? 'is-active' : ''}`}
                        value={department}
                        onChange={(e) => { setDepartment(e.target.value); recordRecentPick('department', e.target.value); }}
                    >
                        <option value="All">All departments</option>
                        <RecentOptions kind="department" items={departmentOptions} />
                    </select>
                    <select
                        className={`task-filter-select ${band !== 'All' ? 'is-active' : ''}`}
                        value={band}
                        onChange={(e) => setBand(e.target.value)}
                    >
                        {BAND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select
                        className={`task-filter-select ${sort !== 'name' ? 'is-active' : ''}`}
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                    >
                        {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {activeFilterCount > 0 && (
                        <button className="task-filter-clear" onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} /> Clear ({activeFilterCount})
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="control-card text-center" style={{ padding: '40px' }}>
                    <FontAwesomeIcon icon={faChartSimple} size="2x" className="text-muted mb-10" />
                    <p className="text-muted">Scoring the team...</p>
                </div>
            ) : grouped.length === 0 ? (
                <div className="control-card text-center" style={{ padding: '40px' }}>
                    <FontAwesomeIcon icon={faUsers} size="2x" className="text-muted mb-10" />
                    <p className="text-muted">
                        {activeFilterCount > 0
                            ? 'Nobody matches these filters'
                            : 'No employees to score for this period'}
                    </p>
                    {activeFilterCount > 0 && (
                        <button className="gts-btn secondary" onClick={clearFilters}>Reset filters</button>
                    )}
                </div>
            ) : (
                <div key={viewKey}>
                    {grouped.map(([deptName, members]) => (
                        <div className="wl-dept-section" key={deptName}>
                            <div className="wl-dept-title">
                                {deptName}
                                <span className="wl-dept-count">{members.length}</span>
                            </div>
                            <div className="wl-grid is-uniform">
                                {members.map((emp, idx) => (
                                    <ScoreCard
                                        key={emp._id}
                                        emp={emp}
                                        idx={idx}
                                        onOpen={() => { recentSearch.save(); navigate(`/performance/${emp._id}`); }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * One person's card. The whole card is the link — unlike the workload board's
 * card, which expands in place, the detail here is a page of its own.
 */
const ScoreCard = ({ emp, idx, onOpen }) => {
    const c = emp.counts;
    const unscored = emp.score === null;

    return (
        <div
            className={`wl-card perf-card ${emp.accent} ${unscored ? 'is-unscored' : ''}`}
            style={{ animationDelay: `${Math.min(idx * 18, 260)}ms` }}
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
            }}
            title={`See every task behind ${emp.name}'s score`}
        >
            <div className="wl-card-head">
                <Avatar name={emp.name} profilePic={emp.profilePic} className="assignee-avatar" />
                <div className="wl-identity">
                    <span className="wl-name">{emp.name}</span>
                    <span className="wl-role">
                        {[emp.employeeId, orgLabel(emp.jobTitle) || emp.role].filter(Boolean).join(' · ')}
                    </span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="perf-go" />
            </div>

            <div className="perf-score-block">
                {unscored ? (
                    <>
                        <span className="perf-score is-empty">—</span>
                        <span className="perf-score-sub">No deadlines this period</span>
                    </>
                ) : (
                    <>
                        <span className="perf-score">{emp.score}<small>%</small></span>
                        <span className="perf-score-sub">
                            on time · {emp.settled} task{emp.settled === 1 ? '' : 's'} settled
                        </span>
                    </>
                )}
            </div>

            <div className="wl-badges">
                {c.onTime > 0 && <span className="task-status-badge completed">{c.onTime} on time</span>}
                {c.late > 0 && <span className="task-status-badge onhold">{c.late} late</span>}
                {c.missed > 0 && <span className="task-status-badge blocked">{c.missed} missed</span>}
                {c.open > 0 && <span className="task-status-badge today">{c.open} in hand</span>}
                {c.total === 0 && <span className="emp-none-pill">No tasks in this period</span>}
            </div>
        </div>
    );
};

export default Performance;
